import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Layout } from 'react-grid-layout';
import type { DashboardLayoutKey, DashboardViews, WidgetConfig, WidgetInstance } from '@contracts';
import { DEFAULT_LOGISTICA_PRESET, defaultConfigForWidgetId, defaultWidgetsForView } from '@contracts';
import { DASHBOARD_MODE } from '@/lib/config';
import { useFilters } from '@/lib/filterContext';
import { getCompanies, getUserId, isDashboardAdmin } from '@/services/authService';
import { deleteDashboard, loadDashboard, saveDashboard } from '@/services/dashboardService';
import { ScopeSaveModal } from '@/components/ScopeSaveModal';

/** Capa donde persistir un cambio: 'user' (mi vista) o 'company' (default de 1+ compañías). */
export interface SaveTarget {
  scope: 'user' | 'company';
  companyIds?: number[];
}

/** Persiste a la capa indicada. 'company' con varias empresas = un PUT por empresa (≤3). */
function persistTo(payload: DashboardViews, scope: 'user' | 'company', companyIds?: number[]): void {
  if (scope === 'company' && companyIds?.length) {
    companyIds.forEach((cid) => saveDashboard(payload, { scope: 'company', companyId: cid }).catch(() => {}));
  } else {
    saveDashboard(payload, { scope: 'user' }).catch(() => {});
  }
}

// La versión (vN) invalida cachés viejas cuando cambia el preset por default.
// El modo logística usa su propio namespace para no mezclarse con ventas.
const STORAGE_PREFIX =
  DASHBOARD_MODE === 'logistica' ? 'portal_dashboard_logistica_v3' : 'portal_dashboard_views';
// `ventasSb` = layout de "Ventas" cuando SOLO está SmartBamboo (default propio).
const ALL_KEYS: DashboardLayoutKey[] = ['ventas', 'vendedor', 'ventasSb'];

// Cache POR-USUARIO: evita que un usuario herede el layout de otro en el mismo
// navegador. El backend (BridgeHub) es la fuente de verdad; esto es solo cache.
const storageKey = (): string => `${STORAGE_PREFIX}:${getUserId() || 'anon'}`;

type ViewWidgets = Record<DashboardLayoutKey, WidgetInstance[]>;

/** Widgets por clave de layout: localStorage (cache por-usuario) → default. */
function loadViews(): ViewWidgets {
  let stored: DashboardViews = {};
  try {
    const raw = localStorage.getItem(storageKey());
    if (raw) stored = JSON.parse(raw) as DashboardViews;
  } catch {
    /* ignore */
  }
  // Modo logística: arranca con el preset de logística en la vista 'ventas'.
  const ventasDefault =
    DASHBOARD_MODE === 'logistica'
      ? DEFAULT_LOGISTICA_PRESET.widgets.map((w) => ({ ...w }))
      : defaultWidgetsForView('ventas');
  return {
    ventas: stored.ventas?.length ? stored.ventas : ventasDefault,
    vendedor: stored.vendedor?.length ? stored.vendedor : defaultWidgetsForView('vendedor'),
    ventasSb: stored.ventasSb?.length ? stored.ventasSb : defaultWidgetsForView('ventasSb'),
  };
}

/** Preset de FÁBRICA (sistema) de una vista. En logística, 'ventas' usa el preset de logística. */
function systemDefaultFor(view: DashboardLayoutKey): WidgetInstance[] {
  return DASHBOARD_MODE === 'logistica' && view === 'ventas'
    ? DEFAULT_LOGISTICA_PRESET.widgets.map((w) => ({ ...w }))
    : defaultWidgetsForView(view);
}

interface DashboardState {
  widgets: WidgetInstance[];
  editMode: boolean;
  setEditMode: (b: boolean) => void;
  // `save` (admin): persiste la vista al instante en esa capa (mi perfil / 1+ compañías). Sin
  // él, el cambio se acumula y se guarda al salir de edición (gerente/regional).
  addWidget: (config: WidgetConfig, save?: SaveTarget) => void;
  updateWidget: (id: string, config: WidgetConfig, save?: SaveTarget) => void;
  removeWidget: (id: string) => void;
  /** Restaura la config interna de un widget a su default de preset (si lo tiene). */
  resetWidget: (id: string) => void;
  /** ¿El widget tiene un default de preset que se pueda restaurar? */
  hasDefault: (id: string) => boolean;
  setLayouts: (layouts: Layout[]) => void;
  resetToDefault: (scope?: 'user' | 'company', companyIds?: number[]) => void;
}

const Ctx = createContext<DashboardState | null>(null);

let idSeq = 0;
const newId = (): string => `w-${Date.now()}-${idSeq++}`;

export function DashboardProvider({ children }: { children: ReactNode }) {
  const { view, companies } = useFilters();
  // SOLO SmartBamboo → layout propio de "Ventas" (ventasSb); si no, la vista normal.
  const sbOnly = companies.length > 0 && companies.every((c) => c === 'sb');
  const layoutKey: DashboardLayoutKey = view === 'ventas' && sbOnly ? 'ventasSb' : view;
  const [views, setViews] = useState<ViewWidgets>(loadViews);
  const [editMode, setEditModeState] = useState(false);
  // Admin: al salir de edición se pregunta dónde guardar (mi vista / default de compañía).
  // Estas son las vistas pendientes de persistir mientras se decide en el modal.
  const [pendingSave, setPendingSave] = useState<DashboardViews | null>(null);

  const layoutKeyRef = useRef(layoutKey);
  layoutKeyRef.current = layoutKey;
  const viewsRef = useRef(views);
  viewsRef.current = views;
  // Claves de layout con cambios sin persistir en BridgeHub (se guardan al salir de edición).
  const dirtyRef = useRef<Set<DashboardLayoutKey>>(new Set());
  // Capa elegida en ESTA sesión de edición (en el guardado por-widget). Si ya se eligió, al
  // salir de edición se reusa sin volver a preguntar "¿dónde guardar?" — el admin ya decidió.
  const lastScopeRef = useRef<{ scope: 'user' | 'company'; companyIds?: number[] } | null>(null);

  const widgets = views[layoutKey];

  // Cache local instantáneo del mapa completo por vista (por-usuario).
  useEffect(() => {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(views));
    } catch {
      /* ignore */
    }
  }, [views]);

  // Al montar: cargar el tablero guardado del usuario (BridgeHub manda sobre el local).
  useEffect(() => {
    loadDashboard()
      .then((remote) => {
        if (!remote) return; // null = sin conexión → conserva la caché local
        // Respuesta OK del BFF MANDA: una vista sin doc resuelto = FÁBRICA, no la caché vieja
        // (si la compañía se restauró, el otro usuario debe caer al preset, no a su localStorage).
        setViews(() => ({
          ventas: remote.ventas?.length ? remote.ventas : systemDefaultFor('ventas'),
          vendedor: remote.vendedor?.length ? remote.vendedor : systemDefaultFor('vendedor'),
          ventasSb: remote.ventasSb?.length ? remote.ventasSb : systemDefaultFor('ventasSb'),
        }));
      })
      .catch(() => {
        /* sin conexión → queda el local */
      });
  }, []);

  // Muta SOLO la vista activa y la marca como sucia.
  const mutateActive = useCallback((fn: (ws: WidgetInstance[]) => WidgetInstance[]) => {
    const v = layoutKeyRef.current;
    dirtyRef.current.add(v);
    setViews((cur) => ({ ...cur, [v]: fn(cur[v]) }));
  }, []);

  // Muta la vista activa y la PERSISTE de inmediato en la capa elegida (mi perfil / compañía).
  // Lee el valor fresco de viewsRef (no hay setViews previo en este flujo) y limpia su dirty
  // para que al salir de edición no se vuelva a preguntar por este cambio.
  const mutateActiveAndSave = useCallback(
    (fn: (ws: WidgetInstance[]) => WidgetInstance[], scope: 'user' | 'company', companyIds?: number[]) => {
      const v = layoutKeyRef.current;
      const nextV = fn(viewsRef.current[v]);
      setViews((cur) => ({ ...cur, [v]: nextV }));
      persistTo({ [v]: nextV }, scope, companyIds);
      dirtyRef.current.delete(v);
      lastScopeRef.current = { scope, companyIds };
    },
    [],
  );

  // Salir de modo edición → persistir las vistas tocadas. Admin: se pregunta la capa
  // (mi vista / default de compañía) vía modal. Gerente/regional: directo a su vista.
  const setEditMode = useCallback((b: boolean) => {
    setEditModeState((prev) => {
      if (!prev && b) lastScopeRef.current = null; // empieza una sesión de edición limpia
      if (prev && !b && dirtyRef.current.size) {
        const payload: DashboardViews = {};
        ALL_KEYS.forEach((v) => {
          if (dirtyRef.current.has(v)) payload[v] = viewsRef.current[v];
        });
        dirtyRef.current.clear();
        const ls = lastScopeRef.current;
        if (!isDashboardAdmin()) saveDashboard(payload).catch(() => {});
        else if (ls) persistTo(payload, ls.scope, ls.companyIds); // ya eligió capa → reusar
        else setPendingSave(payload); // primera decisión de capa → preguntar una vez
      }
      return b;
    });
  }, []);

  // El admin eligió capa en el modal → persistir ahí. Cerrar = guardar en mi vista (seguro).
  const confirmScopeSave = useCallback(
    (scope: 'user' | 'company', companyIds?: number[]) => {
      setPendingSave((p) => {
        if (p) persistTo(p, scope, companyIds);
        return null;
      });
    },
    [],
  );

  const addWidget = useCallback(
    (config: WidgetConfig, save?: SaveTarget) => {
      const fn = (ws: WidgetInstance[]) => {
        const maxY = ws.reduce((m, w) => Math.max(m, w.layout.y + w.layout.h), 0);
        const w = config.visualization === 'kpi' ? 3 : 6;
        const h = config.visualization === 'kpi' ? 2 : 4;
        return [...ws, { id: newId(), config, layout: { x: 0, y: maxY, w, h } }];
      };
      if (save) mutateActiveAndSave(fn, save.scope, save.companyIds);
      else mutateActive(fn);
    },
    [mutateActive, mutateActiveAndSave],
  );

  const updateWidget = useCallback(
    (id: string, config: WidgetConfig, save?: SaveTarget) => {
      const fn = (ws: WidgetInstance[]) => ws.map((w) => (w.id === id ? { ...w, config } : w));
      if (save) mutateActiveAndSave(fn, save.scope, save.companyIds);
      else mutateActive(fn);
    },
    [mutateActive, mutateActiveAndSave],
  );

  const removeWidget = useCallback(
    (id: string) => {
      mutateActive((ws) => ws.filter((w) => w.id !== id));
    },
    [mutateActive],
  );

  const resetWidget = useCallback(
    (id: string) => {
      const def = defaultConfigForWidgetId(id);
      if (!def) return; // widget agregado por el usuario: sin default que restaurar
      mutateActive((ws) => ws.map((w) => (w.id === id ? { ...w, config: { ...def } } : w)));
    },
    [mutateActive],
  );

  const hasDefault = useCallback((id: string) => defaultConfigForWidgetId(id) != null, []);

  const setLayouts = useCallback(
    (layouts: Layout[]) => {
      mutateActive((ws) =>
        ws.map((w) => {
          const l = layouts.find((x) => x.i === w.id);
          return l ? { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : w;
        }),
      );
    },
    [mutateActive],
  );

  // "Restaurar" = quitar MI override personal → caer a la capa de compañía (lo que el admin
  // publicó) o, si no hay, al preset del sistema. Borra en el BFF y recarga lo resuelto.
  const resetToDefault = useCallback(async (scope: 'user' | 'company' = 'user', companyIds?: number[]) => {
    // El DELETE ya devuelve las vistas resueltas (compañía/sistema) → un solo round-trip.
    const remote = await deleteDashboard(scope === 'company' ? { scope, companyIds } : undefined).catch(() => null);
    dirtyRef.current.clear();
    setViews(() => ({
      ventas: remote?.ventas?.length ? remote.ventas : systemDefaultFor('ventas'),
      vendedor: remote?.vendedor?.length ? remote.vendedor : systemDefaultFor('vendedor'),
      ventasSb: remote?.ventasSb?.length ? remote.ventasSb : systemDefaultFor('ventasSb'),
    }));
  }, []);

  return (
    <Ctx.Provider
      value={{ widgets, editMode, setEditMode, addWidget, updateWidget, removeWidget, resetWidget, hasDefault, setLayouts, resetToDefault }}
    >
      {children}
      {pendingSave && (
        <ScopeSaveModal
          companies={getCompanies()}
          onConfirm={confirmScopeSave}
          onClose={() => confirmScopeSave('user')}
        />
      )}
    </Ctx.Provider>
  );
}

export function useDashboard(): DashboardState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useDashboard fuera de DashboardProvider');
  return ctx;
}
